import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { Tag } from '../../types';
import tagService from '../../services/tagService';

export type TagOption = Pick<Tag, 'id' | 'titulo' | 'color'>;

interface TagsMultiSelectProps {
  value?: number[] | any[];
  onChange: (tags: number[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

const TagsMultiSelect: React.FC<TagsMultiSelectProps> = ({
  value = [],
  onChange,
  label = 'Tags',
  placeholder = 'Selecione as tags',
  disabled = false,
  error = false,
  helperText,
  size = 'small',
  fullWidth = true,
}) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const response = await tagService.getAll({ limit: 1000 });
      setTags((response.data || []).filter((tag) => tag.ativo));
    } catch (err) {
      console.error('Erro ao carregar tags:', err);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  // Suporte a dados legados onde value pode ser string JSON "[3]", array de objetos [{id: 3}] ou apenas IDs [3]
  const normalizedValue = React.useMemo(() => {
    let rawArray: any[] = [];

    if (Array.isArray(value)) {
      rawArray = value;
    } else if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          rawArray = parsed;
        } else if (parsed !== null && parsed !== undefined) {
          rawArray = [parsed];
        }
      } catch (e) {
        rawArray = [];
      }
    } else if (value !== null && value !== undefined) {
      rawArray = [value];
    }

    return rawArray
      .map((v: any) => (typeof v === 'object' && v !== null ? Number(v.id) : Number(v)))
      .filter((id) => !isNaN(id));
  }, [value]);

  const selectedOptions = tags.filter((t) => normalizedValue.includes(t.id));

  return (
    <Autocomplete
      multiple
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || loading}
      options={tags}
      getOptionLabel={(option) => option.titulo}
      value={selectedOptions}
      onChange={(_, newValue) => {
        onChange(newValue.map(({ id }) => id));
      }}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      sx={{
        '& .MuiOutlinedInput-root': {
          py: '2px !important',
          px: '6px !important',
          minHeight: 32,
          fontSize: '0.75rem',
        },
        '& .MuiAutocomplete-input': {
          py: '2px !important',
          fontSize: '0.75rem',
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || undefined}
          placeholder={selectedOptions.length === 0 ? placeholder : undefined}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <LocalOffer color="action" sx={{ fontSize: 16 }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.titulo}
            size="small"
            sx={{
              bgcolor: option.color,
              color: '#fff',
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 600,
              my: 0.2,
              mr: 0.5,
              '& .MuiChip-deleteIcon': {
                color: 'rgba(255,255,255,0.7)',
                fontSize: 14,
                '&:hover': { color: '#fff' },
              },
            }}
          />
        ))
      }
    />
  );
};

export default TagsMultiSelect;
